import { type LevelProgressNote, type Level } from '@/lib/types';
import { useCallback, useContext, useState } from 'react';
import RubricsPortfolioContext from '@/lib/contexts/RubricsPortfolioContext';
import TextEditor from '@/components/ChallengePortfolio/NormalView/portfolio-item/PortfolioItemFields/FieldRenderer/TypeSpecificFieldRenderers/utils/TextEditor';
import useApi from '@/lib/api/useApi';
import TonalButton from '@/components/TonalButton';
import { AddCircleOutlineOutlined as AddIcon } from '@mui/icons-material';
import useRubricsPortfolioApiEndpoint from '../../../../useRubricsPortfolioApiEndpoint';
import usePartProgressValues from './usePartProgressValues';
import { FormattedMessage } from 'react-intl';

type AddLevelProgressNoteProps = {
  level: Level;
};

const AddLevelProgressNote = ({ level }: AddLevelProgressNoteProps) => {
  const api = useApi();
  const rubricsPortfolioApiEndpoint = useRubricsPortfolioApiEndpoint();

  const [, setPortfolio] = useContext(RubricsPortfolioContext);
  const [partStudentProgress, partCoachProgress] = usePartProgressValues(level);

  const [formOpen, setFormOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const saveNewNote = useCallback(
    async (value: string | null): Promise<void> => {
      if (value === null) {
        setFormOpen(false);

        return;
      }

      setSaving(true);

      const levelProgressNote = await api.post<LevelProgressNote>(
        `${rubricsPortfolioApiEndpoint}/level-progress-notes/${level.id}`,
        {
          content: value,
          studentProgress: partStudentProgress,
          coachProgress: partCoachProgress,
        },
      );

      setPortfolio(portfolio => {
        if (typeof portfolio.levelProgressNotes![level.id] === 'undefined') {
          portfolio.levelProgressNotes![level.id] = {};
        }

        portfolio.levelProgressNotes![level.id][levelProgressNote.id] =
          levelProgressNote;
      });

      setSaving(false);
      setFormOpen(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [level.id],
  );

  if (formOpen) {
    return <TextEditor onSave={saveNewNote} disabled={saving} />;
  }

  return (
    <TonalButton onClick={() => setFormOpen(true)} startIcon={<AddIcon />}>
      <FormattedMessage id="portfolio.level.attachments.level_progress_notes.add.button" />
    </TonalButton>
  );
};

export default AddLevelProgressNote;
